import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, BookMarked, ExternalLink, ShoppingBag, Truck } from "lucide-react";
import api from "@/lib/api";

const CATEGORIES = ["All", "Primary TET", "Upper Primary TET", "C-TET", "Group C", "Group D", "SSC", "Banking", "General"];

export default function PrintedBooksPage() {
  const [books, setBooks] = useState([]);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchBooks(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== "All") params.append("category", category);
      const { data } = await api.get(`/printed-books?${params.toString()}`);
      setBooks(data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const filtered = search ? books.filter(b => b.title.toLowerCase().includes(search.toLowerCase())) : books;

  return (
    <div className="page-with-nav bg-white min-h-screen" data-testid="printed-books-page">
      {/* Header */}
      <div className="bg-[#D97706] px-4 pt-4 pb-5 text-white">
        <div className="flex items-center gap-2 mb-1">
          <BookMarked size={20} />
          <h1 className="text-xl font-black" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Printed Books
          </h1>
        </div>
        <p className="text-xs text-white/80">Hard copy books delivered to your doorstep</p>
        <div className="relative mt-3">
          <Search className="absolute left-3 top-3 text-amber-300" size={16} />
          <input
            data-testid="printed-books-search"
            type="text"
            placeholder="Search books..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-amber-600 border-b-2 border-amber-500 pl-9 pr-4 py-2.5 text-sm text-white placeholder-amber-300 focus:outline-none focus:border-white transition-none"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="bg-amber-50 px-4 py-3">
        <div className="flex overflow-x-auto gap-2 scrollbar-hide">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`flex-shrink-0 text-xs font-bold px-3 py-2 transition-none ${
                category === cat ? "bg-[#D97706] text-white" : "bg-white text-slate-600 border border-slate-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Delivery badge */}
      <div className="px-4 py-2 bg-green-50 flex items-center gap-2">
        <Truck size={14} className="text-green-600" />
        <span className="text-[10px] font-bold text-green-700">Free delivery across India &middot; Cash on Delivery available</span>
      </div>

      {/* Books List */}
      <div className="px-4 py-4">
        <p className="text-xs text-slate-500 mb-3">{filtered.length} books available</p>
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <BookMarked size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-400">No printed books found</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="printed-books-list">
            {filtered.map((book) => {
              const discount = book.mrp > book.price ? Math.round(((book.mrp - book.price) / book.mrp) * 100) : 0;
              const inStock = book.in_stock !== false;
              return (
                <div key={book.id} data-testid={`printed-book-${book.id}`} className="bg-white border border-slate-100">
                  <div className="flex gap-3 p-3">
                    {/* Book Cover */}
                    <div className="w-28 aspect-[3/4] bg-slate-100 flex-shrink-0 overflow-hidden">
                      {book.image_url ? (
                        <img src={book.image_url} alt={book.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-[#D97706]/10 flex items-center justify-center">
                          <BookMarked size={28} className="text-[#D97706]" />
                        </div>
                      )}
                    </div>

                    {/* Book Info */}
                    <div className="flex-1 flex flex-col justify-between py-1">
                      <div>
                        <span className="text-[10px] font-bold uppercase text-[#D97706]">{book.category}</span>
                        <h3 className="text-sm font-bold text-slate-900 mt-0.5 line-clamp-2">{book.title}</h3>
                        {book.author && <p className="text-xs text-slate-500 mt-0.5">by {book.author}</p>}
                        {book.pages && <p className="text-[10px] text-slate-400 mt-0.5">{book.pages}</p>}
                      </div>

                      {/* Price */}
                      <div className="mt-2">
                        <div className="flex items-center gap-2">
                          <span className="font-black text-lg text-slate-900">&#8377;{book.price}</span>
                          {discount > 0 && (
                            <>
                              <span className="text-xs text-slate-400 line-through">&#8377;{book.mrp}</span>
                              <span className="bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5">{discount}% OFF</span>
                            </>
                          )}
                        </div>
                        <span className={`text-[10px] font-bold ${inStock ? "text-green-600" : "text-red-500"}`}>
                          {inStock ? "In Stock" : "Out of Stock"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  {book.description && (
                    <p className="px-3 pb-2 text-xs text-slate-500 line-clamp-2">{book.description}</p>
                  )}

                  {/* Order Now Button */}
                  <div className="px-3 pb-3">
                    {inStock && book.razorpay_url ? (
                      <a
                        href={book.razorpay_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`order-now-${book.id}`}
                        className="w-full bg-[#D97706] text-white font-bold py-3.5 text-sm flex items-center justify-center gap-2 transition-none hover:bg-[#B45309] no-underline block text-center"
                      >
                        <ShoppingBag size={16} /> Order Now - &#8377;{book.price}
                      </a>
                    ) : inStock ? (
                      <div className="w-full bg-slate-200 text-slate-500 font-bold py-3.5 text-sm flex items-center justify-center gap-2">
                        <ShoppingBag size={16} /> Coming Soon
                      </div>
                    ) : (
                      <div className="w-full bg-red-50 text-red-500 font-bold py-3.5 text-sm flex items-center justify-center gap-2">
                        Out of Stock
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
